import { isExpiredExpiryDate } from "./pathExpiryThreshold";

/** Available quantity at or below this value is treated as low stock (includes 0 = out of stock). */
export const DEFAULT_PATH_LOW_STOCK_QUANTITY_THRESHOLD = 10;

/** Default critical level for pathology inventory add/edit and UI when backend has 0. */
export const DEFAULT_PATH_CRITICAL_LEVEL = 10;

export const resolvePathCriticalLevel = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return DEFAULT_PATH_CRITICAL_LEVEL;
  }
  return numeric;
};

export const PATH_LOW_STOCK_ALERT_SESSION_PREFIX = "hms_path_low_stock_alert_shown";

export const getPathLowStockQuantityThresholdStorageKey = (organizationId) =>
  organizationId
    ? `hms_path_low_stock_quantity_threshold_${organizationId}`
    : "hms_path_low_stock_quantity_threshold_global";

export const readPathLowStockQuantityThreshold = (organizationId) => {
  try {
    const stored = localStorage.getItem(
      getPathLowStockQuantityThresholdStorageKey(organizationId)
    );
    if (stored == null || stored === "") return DEFAULT_PATH_LOW_STOCK_QUANTITY_THRESHOLD;
    const value = Number(stored);
    return Number.isFinite(value) && value >= 0
      ? value
      : DEFAULT_PATH_LOW_STOCK_QUANTITY_THRESHOLD;
  } catch {
    return DEFAULT_PATH_LOW_STOCK_QUANTITY_THRESHOLD;
  }
};

export const getPathLowStockAlertSessionKey = (userId) =>
  `${PATH_LOW_STOCK_ALERT_SESSION_PREFIX}_${userId ?? "unknown"}`;

export const hasShownPathLowStockAlertThisSession = (userId) =>
  sessionStorage.getItem(getPathLowStockAlertSessionKey(userId)) === "1";

export const markPathLowStockAlertShown = (userId) => {
  sessionStorage.setItem(getPathLowStockAlertSessionKey(userId), "1");
};

export const clearPathLowStockAlertSession = (userId) => {
  sessionStorage.removeItem(getPathLowStockAlertSessionKey(userId));
};

export const canShowPathLowStockAlert = (roleName) => {
  const role = String(roleName || "").toLowerCase();
  const key = role.replace(/[\s_-]+/g, "");
  return (
    key === "pathadmin" ||
    key === "pathologyadmin" ||
    key === "pathdesk" ||
    role === "admin" ||
    role === "superadmin" ||
    role === "super_admin"
  );
};

/**
 * Build low-stock list from raw path inventory rows (non-expired qty grouped by item).
 */
export const buildLowStockPathItemList = (
  inventoryItems,
  quantityThreshold = DEFAULT_PATH_LOW_STOCK_QUANTITY_THRESHOLD
) => {
  const grouped = new Map();

  (inventoryItems || []).forEach((item) => {
    const expiryDate =
      item.expirationDate || item.expiryDate || item.expiry || item.expirydate;
    if (isExpiredExpiryDate(expiryDate)) return;

    const bomId = item.bomId ?? item.bom_id;
    const name = item.name || item.bomName || "Unknown";
    const key = bomId != null ? `id-${bomId}` : `name-${name}`;
    const qty = parseInt(item.quantity, 10) || 0;

    if (!grouped.has(key)) {
      grouped.set(key, {
        bomId: bomId ?? null,
        name,
        totalQuantity: 0,
        batchCount: 0,
        criticalLevel: item.criticalLevel ?? null,
        batches: [],
      });
    }

    const entry = grouped.get(key);
    entry.totalQuantity += qty;
    entry.batchCount += 1;
    if (entry.criticalLevel == null && item.criticalLevel != null) {
      entry.criticalLevel = item.criticalLevel;
    }
    const batchNumber = item.batchNumber || item.batch_number;
    if (batchNumber) {
      entry.batches.push({
        batchNumber,
        quantity: qty,
        expiryDate,
      });
    }
  });

  const pickDisplayBatchNumber = (batches) => {
    if (!batches?.length) return null;
    const sorted = [...batches].sort((a, b) => {
      const dateA = a.expiryDate ? new Date(a.expiryDate).getTime() : Infinity;
      const dateB = b.expiryDate ? new Date(b.expiryDate).getTime() : Infinity;
      return dateA - dateB;
    });
    return sorted[0].batchNumber;
  };

  return Array.from(grouped.values())
    .filter((m) => m.totalQuantity <= quantityThreshold)
    .sort((a, b) => a.totalQuantity - b.totalQuantity)
    .map((m) => ({
      bomId: m.bomId,
      name: m.name,
      totalQuantity: m.totalQuantity,
      batchCount: m.batchCount,
      criticalLevel: m.criticalLevel ?? null,
      batchNumber: pickDisplayBatchNumber(m.batches),
      status: m.totalQuantity === 0 ? "Out of stock" : "Low stock",
    }));
};

export const normalizeLowStockPathItemList = (data) => {
  const raw =
    data?.items ??
    data?.pathItems ??
    data?.lowStockPathItems ??
    data?.lowStockItems ??
    data?.data?.items ??
    (Array.isArray(data) ? data : []);

  if (!Array.isArray(raw)) return [];

  return raw
    .map((item) => ({
      bomId: item.bomId ?? item.bom_id ?? item.id ?? null,
      name: item.name || item.itemName || item.bomName || "Unknown",
      totalQuantity:
        item.totalQuantity ??
        item.quantity ??
        item.availableQuantity ??
        0,
      batchCount: item.batchCount ?? item.batch_count ?? null,
      batchNumber: item.batchNumber || item.batch_number || null,
      criticalLevel: item.criticalLevel ?? null,
      status:
        item.status ||
        (Number(item.totalQuantity ?? item.quantity) === 0
          ? "Out of stock"
          : "Low stock"),
    }))
    .sort((a, b) => a.totalQuantity - b.totalQuantity);
};

export const getOutOfStockPathItems = (items = []) =>
  items.filter((m) => Number(m.totalQuantity) === 0);

export const countOutOfStockPathItems = (items = []) =>
  getOutOfStockPathItems(items).length;

/** Map GET /dashboard/top-oos-path-items batches into grouped low-stock rows. */
export const normalizeTopOOSPathItemBatches = (topOOSPathItems = []) => {
  if (!Array.isArray(topOOSPathItems) || topOOSPathItems.length === 0) return [];

  const grouped = new Map();

  topOOSPathItems.forEach((item) => {
    const bomId = item.bomId ?? item.bom_id ?? null;
    const name = item.name || item.itemName || "Unknown";
    const key = bomId != null ? `id-${bomId}` : `name-${name}`;
    const qty = parseInt(item.quantity, 10) || 0;
    const batchNumber = item.batchNumber || item.batch_number || null;

    if (!grouped.has(key)) {
      grouped.set(key, {
        bomId,
        name,
        totalQuantity: 0,
        batchCount: 0,
        batchNumber,
        batches: [],
      });
    }

    const entry = grouped.get(key);
    entry.totalQuantity += qty;
    entry.batchCount += 1;
    if (batchNumber) {
      entry.batches.push({ batchNumber, quantity: qty });
    }
  });

  const pickDisplayBatchNumber = (batches, fallback) => {
    if (!batches?.length) return fallback;
    return batches[0].batchNumber || fallback;
  };

  return Array.from(grouped.values())
    .sort((a, b) => a.totalQuantity - b.totalQuantity)
    .map((m) => ({
      bomId: m.bomId,
      name: m.name,
      totalQuantity: m.totalQuantity,
      batchCount: m.batchCount,
      batchNumber: pickDisplayBatchNumber(m.batches, m.batchNumber),
      criticalLevel: null,
      status: m.totalQuantity === 0 ? "Out of stock" : "Low stock",
    }));
};

export const mapLowStockToDashboardPathRows = (items = [], limit = 5) =>
  items.slice(0, limit).map((m, index) => ({
    id: m.bomId ?? `path-low-stock-${index}`,
    name: m.name,
    quantity: m.totalQuantity,
    manufacturer: null,
    batchNumber:
      m.batchNumber ||
      (m.batchCount > 0 ? `${m.batchCount} batch(es)` : null),
    status: m.status || (m.totalQuantity === 0 ? "Out of stock" : "Low stock"),
  }));
